import React from 'react';
import { ArrowUpDown, CheckCircle2, Clock, MessageSquareText, PackagePlus, Play, SquarePen, XCircle } from 'lucide-react';
import { formatDateForGroup } from '../../../utils/date';
import type { WorkOrderStatus } from '../../../types/workOrder';
import type { InventoryItem } from '../../../types/inventory';
import type { DerivedWorkOrder } from '../hooks/useWorkOrdersPage';
import { useLanguage } from '../../../features/language/useLanguage';
import { RoastWeightCell } from '../../../components/atoms/RoastWeightCell';
import { RoastTypeCell } from '../../../components/atoms/RoastTypeCell';

interface WorkOrdersTableProps {
  orders: DerivedWorkOrder[];
  shouldGroup: boolean;
  groupedOrders: Record<string, DerivedWorkOrder[]>;
  sortedDates: string[];
  sortConfig: { key: keyof DerivedWorkOrder; direction: 'asc' | 'desc' } | null;
  handleSort: (key: keyof DerivedWorkOrder) => void;
  roastTypeLabelMap: Record<string, string>;
  formatDate: (value?: string) => string;
  formatNumber: (value: number | null | undefined, digits?: number) => string | null;
  formatPercent: (value: number | null | undefined) => string | null;
  onEditOrder: (id: string) => void;
  onStatusChange: (id: string, status: WorkOrderStatus) => void;
  onCancelOrder: (order: DerivedWorkOrder) => void;
  onDeleteOrder: (order: DerivedWorkOrder) => void;
  onMoveToInventory: (order: DerivedWorkOrder) => void;
  inventoryItems: InventoryItem[];
  canWrite?: boolean;
  canDelete?: boolean;
}

const extractTime = (value?: string): string | null => {
  if (!value) return null;
  const bareMatch = value.match(/^(\d{2}):(\d{2})/);
  if (bareMatch && !value.includes('T')) return `${bareMatch[1]}:${bareMatch[2]}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const hh = String(parsed.getHours()).padStart(2, '0');
  const mm = String(parsed.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20';
    case 'in_progress':
      return 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20';
    case 'scheduled':
      return 'bg-primary-50 text-primary-800 ring-1 ring-inset ring-primary-600/20';
    case 'cancelled':
      return 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20';
    default:
      return 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-600/20';
  }
};

const statusDotClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'in_progress':
      return 'bg-blue-500';
    case 'scheduled':
      return 'bg-primary-500';
    case 'cancelled':
      return 'bg-red-500';
    default:
      return 'bg-yellow-500';
  }
};

export const WorkOrdersTable: React.FC<WorkOrdersTableProps> = ({
  orders,
  shouldGroup,
  groupedOrders,
  sortedDates,
  sortConfig,
  handleSort,
  roastTypeLabelMap,
  formatDate,
  formatNumber,
  formatPercent,
  onEditOrder,
  onStatusChange,
  onCancelOrder,
  onDeleteOrder,
  onMoveToInventory,
  inventoryItems,
  canWrite = true,
  canDelete = true,
}) => {
  const { t } = useLanguage();
  const statusLabel = (status: string) =>
    t(`workOrders.status.${status}`, undefined, status.replace('_', ' '));

  const renderSortIndicator = (key: keyof DerivedWorkOrder) =>
    sortConfig?.key === key ? (
      <ArrowUpDown className="h-3.5 w-3.5 text-primary-600" />
    ) : (
      <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    );

  const headerCell = (key: keyof DerivedWorkOrder, label: string, align: 'left' | 'right' = 'left') => (
    <th
      className={`group px-4 py-3 text-${align} text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100/70 transition-colors`}
      onClick={() => handleSort(key)}
    >
      <div className={`flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        {renderSortIndicator(key)}
      </div>
    </th>
  );

  const renderRow = (order: DerivedWorkOrder) => {
    const shouldShowShrinkage =
      formatNumber(order.shrinkageKg) !== null &&
      formatNumber(order.shrinkagePercent) !== null &&
      !(order.status !== 'completed' && (order.outputWeight ?? 0) === 0);

    const scheduledTime = extractTime(order.time) ?? extractTime(order.startDate);

    return (
      <tr
        key={order.id}
        className="group/row hover:bg-primary-50/30 transition-colors cursor-pointer"
        data-test-id={`wo-row-${order.id}`}
        onClick={() => onEditOrder(order.id)}
      >
        <td className="px-4 py-3.5 whitespace-nowrap text-sm font-semibold text-gray-900 tabular-nums">
          <div className="flex items-center gap-2">
            <span data-test-id={`wo-number-${order.id}`}>{order.workOrderNumber || '—'}</span>
            {order.internalNotes && (
              <div className="group relative">
                <MessageSquareText className="h-4 w-4 text-blue-500" />
                <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200 break-words whitespace-pre-wrap">
                  {order.internalNotes}
                  <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                </div>
              </div>
            )}
          </div>
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap text-sm text-gray-900 max-w-[220px]">
          <div className="flex flex-col leading-tight">
            <span className="font-medium truncate">{order.resolvedItemName || '-'}</span>
            <span className="text-[11px] font-normal text-gray-400 uppercase tracking-wider mt-0.5">
              {order.resolvedItemCode || '-'}
            </span>
          </div>
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap">
          <RoastTypeCell
            roastType={order.roastType}
            roastTypeLabel={order.roastType ? roastTypeLabelMap[order.roastType] ?? order.roastType : '-'}
            agtron={order.roastProfile?.agtron}
          />
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap">
          <RoastWeightCell
            inputWeight={order.inputWeight}
            outputWeight={order.outputWeight}
            shrinkageKg={order.shrinkageKg}
            shrinkagePercent={order.shrinkagePercent}
            showShrinkage={shouldShowShrinkage}
            formatNumber={formatNumber}
            formatPercent={formatPercent}
          />
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap">
          <div className="flex flex-col leading-tight">
            <span className="text-sm text-gray-900 tabular-nums">{formatDate(order.date)}</span>
            {scheduledTime ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 tabular-nums mt-1">
                <Clock className="h-3 w-3" />
                {scheduledTime}
              </span>
            ) : (
              <span className="text-[11px] text-gray-300 mt-1">—</span>
            )}
          </div>
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${statusBadgeClass(order.status)}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${statusDotClass(order.status)}`} />
            {statusLabel(order.status)}
          </span>
        </td>

        <td className="px-4 py-3.5 whitespace-nowrap text-sm font-medium">
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {order.status === 'draft' && canWrite && (
              <button
                onClick={() => onEditOrder(order.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 ring-1 ring-inset ring-blue-600/10 transition-colors"
                data-test-id={`wo-start-button-${order.id}`}
              >
                <Play className="w-4 h-4" />
                {t('workOrders.actions.start')}
              </button>
            )}

            {order.status === 'scheduled' && canWrite && (
              <button
                onClick={() => onStatusChange(order.id, 'completed')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-green-700 bg-green-50 rounded-md hover:bg-green-100 ring-1 ring-inset ring-green-600/10 transition-colors"
                data-test-id={`wo-complete-button-${order.id}`}
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('workOrders.actions.complete')}
              </button>
            )}

            {(order.status === 'draft' || order.status === 'scheduled') && canWrite && (
              <button
                onClick={() => onCancelOrder(order)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-600 bg-red-50 rounded-md hover:bg-red-100 ring-1 ring-inset ring-red-600/10 transition-colors"
                data-test-id={`wo-cancel-button-${order.id}`}
              >
                <XCircle className="w-4 h-4" />
                {t('workOrders.actions.cancel')}
              </button>
            )}

            {order.status === 'cancelled' && canDelete && (
              <button
                onClick={() => onDeleteOrder(order)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-red-700 bg-red-50 rounded-md hover:bg-red-100 ring-1 ring-inset ring-red-600/10 transition-colors"
                data-test-id={`wo-delete-button-${order.id}`}
              >
                <XCircle className="w-4 h-4" />
                {t('workOrders.actions.delete')}
              </button>
            )}

            {order.status === 'completed' && (() => {
              const isAlreadyInInventory = inventoryItems.some(item => item.workOrderId === order.id);
              return (
                <button
                  onClick={() => {
                    if (canWrite) onMoveToInventory(order);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md ring-1 ring-inset transition-colors ${
                    canWrite
                      ? 'text-primary-700 bg-primary-50 hover:bg-primary-100 ring-primary-600/10 cursor-pointer'
                      : 'text-gray-400 bg-gray-50 ring-gray-300/30 cursor-default'
                  }`}
                  title={
                    isAlreadyInInventory
                      ? t('workOrders.actions.editFinishedGoods')
                      : t('workOrders.actions.createFinishedGoods')
                  }
                  data-test-id={`wo-move-inv-button-${order.id}`}
                >
                  {isAlreadyInInventory ? (
                    <SquarePen className="w-4 h-4" />
                  ) : (
                    <PackagePlus className="w-4 h-4" />
                  )}
                  {isAlreadyInInventory ? t('workOrders.actions.editStock') : t('workOrders.actions.stockIn')}
                </button>
              );
            })()}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <table className="min-w-full divide-y divide-gray-200 hidden md:table" data-test-id="work-orders-table">
      <thead className="bg-gray-50/80 backdrop-blur-sm sticky top-0 z-10">
        <tr>
          {headerCell('workOrderNumber', t('workOrders.table.workOrder'))}
          {headerCell('itemCode', t('workOrders.table.item'))}
          {headerCell('roastType', t('workOrders.table.roastType'))}
          {headerCell('inputWeight', t('workOrders.table.roastWeight', undefined, 'Roast (kg)'))}
          {headerCell('date', t('workOrders.table.orderDate'))}
          {headerCell('status', t('workOrders.table.status'))}
          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
            {t('workOrders.table.actions')}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-100">
        {shouldGroup ? (
          sortedDates.map((dateKey) => (
            <React.Fragment key={dateKey}>
              <tr className="bg-gradient-to-r from-primary-50/60 via-primary-50/30 to-transparent">
                <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-primary-800 uppercase tracking-wider">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                    {formatDateForGroup(dateKey)}
                  </span>
                </td>
              </tr>
              {groupedOrders[dateKey].map(renderRow)}
            </React.Fragment>
          ))
        ) : (
          orders.map(renderRow)
        )}
      </tbody>
    </table>
  );
};
